import { AdminLayout } from "./AdminLayout";
import { useState, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

const MockupLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '', contact_name: '', contact_email: '', website_url: '', industry: ''
  });

  useEffect(() => {
    fetchLeads();
  }, [search]);

  const fetchLeads = async () => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/client-mockups/leads`, { params, withCredentials: true });
      setLeads(response.data.data || response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/client-mockups/leads`, formData, { withCredentials: true });
      toast.success('Lead created');
      setIsDialogOpen(false);
      setFormData({ company_name: '', contact_name: '', contact_email: '', website_url: '', industry: '' });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'NEW': 'bg-blue-500/20 text-blue-400',
      'CONTACTED': 'bg-purple-500/20 text-purple-400',
      'MOCKUP_SENT': 'bg-green-500/20 text-green-400',
      'VIEWED': 'bg-yellow-500/20 text-yellow-400',
      'WON': 'bg-emerald-500/20 text-emerald-400',
      'LOST': 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const filteredLeads = statusFilter
    ? leads.filter(l => l.status === statusFilter)
    : leads;

  return (
    <AdminLayout title="Client Mockups">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Leads</h2>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Lead
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Company Name *" required value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})} className="input-field" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Contact Name" value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})} className="input-field" />
                  <input type="email" placeholder="Email *" required value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})} className="input-field" />
                </div>
                <input type="url" placeholder="Website URL" value={formData.website_url}
                  onChange={(e) => setFormData({...formData, website_url: e.target.value})} className="input-field" />
                <input type="text" placeholder="Industry" value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})} className="input-field" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">Create Lead</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input
              type="text"
              placeholder="Search companies, contacts, emails..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-48">
            <option value="">All Statuses</option>
            <option value="NEW">New</option>
            <option value="CONTACTED">Contacted</option>
            <option value="MOCKUP_SENT">Mockup Sent</option>
            <option value="VIEWED">Viewed</option>
            <option value="WON">Won</option>
            <option value="LOST">Lost</option>
          </select>
        </div>

        <div className="card-dashboard overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2E2E2E]">
                <th className="text-left py-3 px-4 text-sm font-medium text-[#8A8A8A]">Company</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#8A8A8A]">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#8A8A8A]">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#8A8A8A]">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[#8A8A8A]">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8 text-[#8A8A8A]">Loading...</td></tr>
              ) : filteredLeads.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-[#8A8A8A]">No leads found</td></tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/admin/client-mockups/leads/${lead.id}`)}
                    className="border-b border-[#2E2E2E] hover:bg-[#2E2E2E] cursor-pointer"
                  >
                    <td className="py-3 px-4 font-medium">{lead.company_name}</td>
                    <td className="py-3 px-4 text-[#8A8A8A]">{lead.contact_name || '-'}</td>
                    <td className="py-3 px-4 text-[#8A8A8A]">{lead.contact_email}</td>
                    <td className="py-3 px-4">
                      <span className={`badge ${getStatusColor(lead.status)} text-xs`}>{lead.status}</span>
                    </td>
                    <td className="py-3 px-4 text-[#8A8A8A] text-sm">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MockupLeads;
